/**
 * AI CONTEXT
 * Purpose: the profile's Artwork tab — every piece tiled at a width the user
 *   sets with a slider, grouped newest-semester-first with per-class labels,
 *   rotate + add-to-portfolio + hide/unhide on hover, a competition badge,
 *   hidden tiles (badged, art untouched) behind a "Show hidden" toggle, and
 *   a fullscreen view that reuses the same grid (and therefore the same
 *   cached images). "Hidden" is the user-facing word for the stored
 *   `isHidden` flag — the field name is frozen into the partner-app
 *   contract, the copy is not.
 *   Clicking a tile opens an in-app lightbox (never a new browser tab).
 *   Artwork letterboxes inside its square tile (contain, centered), never
 *   cover-crops. Controls are a sticky header inside each surface's own
 *   scroll container; the fullscreen gallery renders on a dark background
 *   while the tab stays light. Rotation applies instantly on screen and
 *   saves debounced (0.5s after the last click), flushing on unmount so a
 *   navigation never loses the write.
 * Key deps: studentArtworkTilesHelpers (pure grouping/filtering + write
 *   helpers), ArtworkEditableThumb (zoom, crop, and rotation, saved on the
 *   student artwork), classAtoms (class names/groups), and the shared
 *   square-tile primitives in ArtworkTiles/ (grid, slider, fullscreen).
 *   Deliberately independent of the artwork wall component.
 * Referenced by: StudentProfileTabs (Artwork tab).
 */
import {
  ClearOutlined,
  DeleteOutlined,
  FolderAddOutlined,
  FullscreenOutlined,
  TrophyOutlined,
  UndoOutlined,
} from "@ant-design/icons"
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Spin,
  Tooltip,
  Typography,
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { filter, keyBy, map, mapValues, partition, uniq } from "lodash"
import { type ReactNode, useCallback, useMemo, useState } from "react"
import styled from "styled-components"

import { classAtoms, portfolioAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import ArtworkEditableThumb, {
  ARTWORK_EDIT_SOURCE,
} from "#components/ArtworkTiles/ArtworkEditableThumb"
import {
  FULLSCREEN_BG,
  getTierForTileWidth,
  MAX_TILE_WIDTH,
  secondaryTextStyle,
  SScrollArea,
  SStickyControls,
  STileGrid,
  STileSquare,
  THUMBNAIL_TILE_WIDTH,
  TileWidthSlider,
  useBrowserFullscreen,
} from "#components/ArtworkTiles/artworkTilePrimitives"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentPortfolioConstructor } from "#customTypes/composedTypes/portfolioTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { useOneDoc, useSomeDocs } from "#hooks/jotai/useSubscription"
import { COLORS } from "#styles/colors"
import { type RenditionTier } from "#utils/artworkRenditionHelpers"
import { STANDARD_DATE_FORMAT } from "#utils/dateHelpers"

import {
  applyArtworkTileFilters,
  type ArtworkTile,
  type ArtworkTileFilters,
  type ArtworkTileSection,
  buildArtworkTiles,
  CLASS_GROUP_FILTER_OPTIONS,
  CORE_LEVEL_FILTER_OPTIONS,
  countActiveArtworkTileFilters,
  EMPTY_ARTWORK_TILE_FILTERS,
  groupTilesBySemester,
  selectVisibleArtworkTiles,
} from "./studentArtworkTilesHelpers"

const { RangePicker } = DatePicker

const { Text } = Typography

/** Award gold — the theme has no warning color, so the badge owns its own. */
const COMPETITION_BADGE_BG = "#D4A017"

/**
 * Always-visible corner badge for competition entries — the ribbon the
 * mobile app shows, in desktop form. Hover names the competition(s).
 */
const SCompetitionBadge = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background: ${COMPETITION_BADGE_BG};
`

/**
 * Chip for a hidden piece — the ONLY visual marker by owner ask (Sep 2026):
 * no dimming or grayscale, the art must stay readable. It joins Shared and
 * the other tags in the thumb's bottom row, so the two labels can both be
 * read. The competition ribbon stays in the top-left.
 */
const SHiddenBadge = styled.span`
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background: ${COLORS.GRAY7};
`

const SSectionHeader = styled.div<{ $dark: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 4px 0 6px;
  border-bottom: 1px solid
    ${({ $dark }) => ($dark ? "rgba(255, 255, 255, 0.3)" : COLORS.GRAY3)};
  margin-bottom: 8px;
  font-weight: 600;
  color: ${({ $dark }) => ($dark ? "#fff" : "inherit")};
`

const SClassLabel = styled.div<{ $dark: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 6px 0 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $dark }) => ($dark ? COLORS.GRAY3 : COLORS.GRAY7)};
`

/**
 * One tile. The shared thumb letterboxes the piece and owns zoom, crop, and
 * rotation. Hide, unhide, and add-to-portfolio stay on the tile because they
 * are profile actions. A hidden piece offers Unhide only — no crop or rotate
 * against a piece that is hidden everywhere.
 *
 * A tile whose image fails to load renders nothing: the known cause is
 * filename collisions from same-minute scans, which are duplicates of a piece
 * that did load.
 */
const ArtworkTileImage = ({
  tile,
  studentId,
  tier,
  dark,
  isAdding,
  isHiding,
  onAddToPortfolio,
  onHide,
}: {
  tile: ArtworkTile
  studentId: string
  tier: RenditionTier
  dark: boolean
  isAdding: boolean
  isHiding: boolean
  onAddToPortfolio: ((tile: ArtworkTile) => void) | undefined
  onHide: ((tile: ArtworkTile, isHidden: boolean) => void) | undefined
}) => {
  const { artwork, caption } = tile

  const handleAddToPortfolio = useCallback(
    () => onAddToPortfolio?.(tile),
    [onAddToPortfolio, tile]
  )

  const handleHide = useCallback(() => onHide?.(tile, true), [onHide, tile])

  const handleUnhide = useCallback(() => onHide?.(tile, false), [onHide, tile])

  const competitionTooltip =
    tile.competitionIds.length > 0
      ? [
          `Competition: ${tile.competitionIds.join(", ")}`,
          ...(artwork.competitionOutcome
            ? [`Result: ${artwork.competitionOutcome}`]
            : []),
        ].join(" — ")
      : null

  const profileActions = tile.isHidden ? (
    onHide ? (
      <Tooltip title="Unhide">
        <Button
          size="small"
          icon={<UndoOutlined />}
          onClick={handleUnhide}
          loading={isHiding}
          aria-label={`Unhide ${caption}`}
          data-testid="artwork-tile-unhide"
        />
      </Tooltip>
    ) : null
  ) : (
    <>
      {onAddToPortfolio && tile.showAddToPortfolioCta ? (
        <Tooltip title="Add to portfolio">
          <Button
            size="small"
            icon={<FolderAddOutlined />}
            onClick={handleAddToPortfolio}
            loading={isAdding}
            aria-label={`Add ${caption} to portfolio`}
            data-testid="artwork-tile-add-to-portfolio"
          />
        </Tooltip>
      ) : null}
      {onHide ? (
        <Popconfirm
          title="Hide this piece? It will be hidden everywhere but not deleted."
          onConfirm={handleHide}
          okText="Hide"
        >
          <Tooltip title="Hide">
            <Button
              size="small"
              icon={<DeleteOutlined />}
              loading={isHiding}
              aria-label={`Hide ${caption}`}
              data-testid="artwork-tile-hide"
            />
          </Tooltip>
        </Popconfirm>
      ) : null}
    </>
  )

  const lightboxActions = tile.isHidden ? (
    onHide ? (
      <Tooltip title="Unhide">
        <Button
          icon={<UndoOutlined />}
          onClick={handleUnhide}
          loading={isHiding}
          aria-label={`Unhide ${caption}`}
          data-testid="artwork-lightbox-unhide"
        />
      </Tooltip>
    ) : null
  ) : (
    <>
      {onAddToPortfolio && tile.showAddToPortfolioCta ? (
        <Tooltip title="Add to portfolio">
          <Button
            icon={<FolderAddOutlined />}
            onClick={handleAddToPortfolio}
            loading={isAdding}
            aria-label={`Add ${caption} to portfolio`}
            data-testid="artwork-lightbox-add-to-portfolio"
          />
        </Tooltip>
      ) : null}
      {onHide ? (
        <Popconfirm
          title="Hide this piece? It will be hidden everywhere but not deleted."
          onConfirm={handleHide}
          okText="Hide"
        >
          <Tooltip title="Hide">
            <Button
              icon={<DeleteOutlined />}
              loading={isHiding}
              aria-label={`Hide ${caption}`}
              data-testid="artwork-lightbox-hide"
            />
          </Tooltip>
        </Popconfirm>
      ) : null}
    </>
  )

  return (
    <STileSquare
      $dark={dark}
      data-testid="artwork-tile-simple"
      data-artwork-id={tile.id}
      data-hidden={tile.isHidden ? "true" : undefined}
    >
      <ArtworkEditableThumb
        studentId={studentId}
        artworkId={artwork.id}
        artwork={artwork}
        source={ARTWORK_EDIT_SOURCE.STUDENT_PROFILE}
        fill
        objectFit="contain"
        tier={tier}
        alt={caption}
        caption={caption}
        viewLabel={`View ${caption} fullscreen`}
        editable={!tile.isHidden}
        extraTileActions={profileActions}
        extraLightboxActions={lightboxActions}
        imageTags={
          tile.isHidden ? (
            <SHiddenBadge data-testid="artwork-tile-hidden-badge">
              Hidden
            </SHiddenBadge>
          ) : undefined
        }
      />
      {competitionTooltip ? (
        <Tooltip title={competitionTooltip}>
          <SCompetitionBadge data-testid="artwork-tile-competition">
            <TrophyOutlined />
          </SCompetitionBadge>
        </Tooltip>
      ) : null}
    </STileSquare>
  )
}

const ArtworkTileGrid = ({
  sections,
  studentId,
  tileWidth,
  dark,
  addingId,
  hidingId,
  onAddToPortfolio,
  onHide,
}: {
  sections: readonly ArtworkTileSection[]
  studentId: string
  tileWidth: number
  dark: boolean
  addingId: string | null
  hidingId: string | null
  onAddToPortfolio: ((tile: ArtworkTile) => void) | undefined
  onHide: ((tile: ArtworkTile, isHidden: boolean) => void) | undefined
}) => {
  const tier = getTierForTileWidth(tileWidth)

  return (
    <Flex col gap={18}>
      {map(sections, (section) => (
        <div
          key={section.key}
          data-testid="artwork-tiles-section"
          data-section-key={section.key}
        >
          <SSectionHeader $dark={dark}>
            <span>{section.title}</span>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                fontWeight: 400,
                ...secondaryTextStyle(dark),
              }}
            >
              {section.subtitle}
            </Text>
          </SSectionHeader>
          {map(section.classBuckets, (bucket) => (
            <div key={bucket.key} data-testid="artwork-tiles-class-bucket">
              <SClassLabel $dark={dark} data-testid="artwork-tiles-class-label">
                <span>{bucket.label}</span>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, ...secondaryTextStyle(dark) }}
                >
                  {bucket.tiles.length}
                </Text>
              </SClassLabel>
              <STileGrid
                $tileWidth={tileWidth}
                data-testid="artwork-tiles-grid"
              >
                {map(bucket.tiles, (tile) => (
                  <ArtworkTileImage
                    key={tile.id}
                    tile={tile}
                    studentId={studentId}
                    tier={tier}
                    dark={dark}
                    isAdding={addingId === tile.id}
                    isHiding={hidingId === tile.id}
                    onAddToPortfolio={onAddToPortfolio}
                    onHide={onHide}
                  />
                ))}
              </STileGrid>
            </div>
          ))}
        </div>
      ))}
    </Flex>
  )
}

const VERSIONS = {
  LATEST: "latest",
  ALL: "all",
} as const

const VERSION_OPTIONS = [
  { label: "Latest versions", value: VERSIONS.LATEST },
  { label: "All versions", value: VERSIONS.ALL },
]

const StudentArtworkTilesSection = ({
  student,
  isLoading,
  onAddToPortfolio,
  onHide,
}: {
  student:
    | Pick<
        StudentConstructor,
        "id" | "name" | "artwork" | "skillHistory" | "dob"
      >
    | undefined
  isLoading: boolean
  /**
   * Marks a piece as in the portfolio (and creates its project). Omitting it
   * hides the hover button.
   */
  onAddToPortfolio?: (tile: ArtworkTile) => Promise<void>
  /**
   * Hides (`isHidden` true) or unhides a piece. Omitting it removes the
   * Hide/Unhide buttons — but not the "Show hidden" checkbox, because viewing
   * hidden pieces is never write-gated.
   */
  onHide?: (tile: ArtworkTile, isHidden: boolean) => Promise<void>
}) => {
  const [tileWidth, setTileWidth] = useState(THUMBNAIL_TILE_WIDTH)
  const [fullscreenTileWidth, setFullscreenTileWidth] = useState(MAX_TILE_WIDTH)
  const [addingId, setAddingId] = useState<string | null>(null)
  const [hidingId, setHidingId] = useState<string | null>(null)
  const [filters, setFilters] = useState<ArtworkTileFilters>(
    EMPTY_ARTWORK_TILE_FILTERS
  )
  const [versions, setVersions] = useState<string>(VERSIONS.ALL)
  const [showHidden, setShowHidden] = useState(false)

  // Class docs give tiles their class name and group (for labels and the
  // class-type filter). Same subscription shape as the old artwork section.
  const classIds = useMemo(
    () =>
      uniq(map(student?.artwork ?? {}, (artwork) => artwork.classId)).filter(
        Boolean
      ),
    [student?.artwork]
  )

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classIds
  )

  const classesById = useMemo(() => keyBy(classes, "id"), [classes])

  // The portfolio doc (one per student, id = studentId) names the projects
  // that artwork pieces belong to, for the tile captions.
  const portfolioDoc = useOneDoc<StudentPortfolioConstructor>(
    portfolioAtoms,
    DOC_TYPES.PORTFOLIOS,
    student?.id ?? null
  )

  const projectTitlesById = useMemo(
    () => mapValues(portfolioDoc?.projects ?? {}, (project) => project.title),
    [portfolioDoc?.projects]
  )

  // antd swallows clicks on a loading button, so a second click cannot land
  // mid-write; the id is cleared in a finally so a failed save re-enables it.
  const handleAddToPortfolio = useCallback(
    (tile: ArtworkTile) => {
      if (!onAddToPortfolio) return
      setAddingId(tile.id)
      void onAddToPortfolio(tile).finally(() => setAddingId(null))
    },
    [onAddToPortfolio]
  )

  // Same pending-id pattern as add-to-portfolio: the loading button swallows
  // a second click, so hide/unhide cannot double-write.
  const handleHide = useCallback(
    (tile: ArtworkTile, isHidden: boolean) => {
      if (!onHide) return
      setHidingId(tile.id)
      void onHide(tile, isHidden).finally(() => setHidingId(null))
    },
    [onHide]
  )

  const allTiles = useMemo(
    () => buildArtworkTiles(student, classesById, projectTitlesById),
    [student, classesById, projectTitlesById]
  )

  // Fullscreen re-opens at max size every time: it exists to look closely.
  // Native fullscreen belongs to the gallery modal, so the hook releases it
  // on every path that takes that modal off screen — including the ones no
  // handler runs on: the loading and empty early returns below (`canShow`)
  // and unmount (switching profile tabs, closing the profile).
  const {
    isFullscreen,
    openFullscreen: openBrowserFullscreen,
    closeFullscreen,
  } = useBrowserFullscreen(!isLoading && allTiles.length > 0)

  const openFullscreen = useCallback(() => {
    setFullscreenTileWidth(MAX_TILE_WIDTH)
    openBrowserFullscreen()
  }, [openBrowserFullscreen])

  const sections = useMemo(() => {
    const filtered = applyArtworkTileFilters(allTiles, filters)
    return groupTilesBySemester(
      selectVisibleArtworkTiles(
        filtered,
        showHidden,
        versions === VERSIONS.LATEST
      )
    )
  }, [allTiles, filters, showHidden, versions])

  const hiddenCount = useMemo(
    () => filter(allTiles, (tile) => tile.isHidden).length,
    [allTiles]
  )

  const { livePieceCount, hiddenPieceCount } = useMemo(() => {
    const [hiddenTiles, liveTiles] = partition(
      sections.flatMap((section) => section.tiles),
      (tile) => tile.isHidden
    )
    return {
      livePieceCount: liveTiles.length,
      hiddenPieceCount: hiddenTiles.length,
    }
  }, [sections])

  const visibleCount = livePieceCount + hiddenPieceCount

  const activeFilterCount = countActiveArtworkTileFilters(filters)

  const dateRangeValue = useMemo<[Dayjs, Dayjs] | null>(
    () =>
      filters.dateRange
        ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]
        : null,
    [filters.dateRange]
  )

  const handleDateRange = useCallback(
    (range: [Dayjs | null, Dayjs | null] | null) => {
      const [start, end] = range ?? [null, null]
      setFilters((previous) => ({
        ...previous,
        dateRange:
          start && end
            ? [start.startOf("day").valueOf(), end.endOf("day").valueOf()]
            : null,
      }))
    },
    []
  )

  const handleLevels = useCallback((levels: string[]) => {
    setFilters((previous) => ({ ...previous, levels }))
  }, [])

  const handleClassGroups = useCallback((classGroups: string[]) => {
    setFilters((previous) => ({ ...previous, classGroups }))
  }, [])

  const handleAgeMin = useCallback((value: number | null) => {
    setFilters((previous) => ({ ...previous, ageMin: value }))
  }, [])

  const handleAgeMax = useCallback((value: number | null) => {
    setFilters((previous) => ({ ...previous, ageMax: value }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_ARTWORK_TILE_FILTERS)
    setVersions(VERSIONS.ALL)
    setShowHidden(false)
  }, [])

  if (isLoading) {
    return (
      <Flex center style={{ padding: 48 }}>
        <Spin />
      </Flex>
    )
  }

  // Only a truly empty artwork map short-circuits: when every piece is
  // hidden the controls must still render, so the "Show hidden" checkbox
  // stays reachable.
  if (allTiles.length === 0) {
    return (
      <Empty
        description="No artwork yet"
        style={{ padding: 32 }}
        data-testid="artwork-tiles-empty"
      />
    )
  }

  const liveTotal = allTiles.length - hiddenCount
  const liveSummary = `${livePieceCount} of ${liveTotal} ${liveTotal === 1 ? "piece" : "pieces"} across ${sections.length} ${sections.length === 1 ? "semester" : "semesters"}`
  const summary =
    visibleCount === 0
      ? "No artwork matches these filters"
      : hiddenPieceCount > 0
        ? `${liveSummary} + ${hiddenPieceCount} hidden`
        : liveSummary

  const buildFilterRow = () => (
    <Flex
      gap={8}
      wrap
      style={{ alignItems: "center" }}
      data-testid="artwork-tiles-filters"
    >
      {/* Wrapped: antd's picker and select do not forward data-* attributes. */}
      <span data-testid="artwork-tiles-filter-date">
        <RangePicker
          value={dateRangeValue}
          onChange={handleDateRange}
          format={STANDARD_DATE_FORMAT}
          allowEmpty={[false, false]}
          placeholder={["Created from", "Created to"]}
          size="small"
        />
      </span>
      <span data-testid="artwork-tiles-filter-level">
        <Select
          mode="multiple"
          allowClear
          size="small"
          placeholder="Level (core)"
          value={[...filters.levels]}
          onChange={handleLevels}
          options={[...CORE_LEVEL_FILTER_OPTIONS]}
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
        />
      </span>
      <span data-testid="artwork-tiles-filter-class-group">
        <Select
          mode="multiple"
          allowClear
          size="small"
          placeholder="Class type"
          value={[...filters.classGroups]}
          onChange={handleClassGroups}
          options={[...CLASS_GROUP_FILTER_OPTIONS]}
          maxTagCount="responsive"
          style={{ minWidth: 160 }}
        />
      </span>
      <InputNumber
        size="small"
        min={0}
        max={120}
        precision={0}
        value={filters.ageMin}
        onChange={handleAgeMin}
        placeholder="Age min"
        aria-label="Minimum age at creation"
        style={{ width: 96 }}
        data-testid="artwork-tiles-filter-age-min"
      />
      <InputNumber
        size="small"
        min={0}
        max={120}
        precision={0}
        value={filters.ageMax}
        onChange={handleAgeMax}
        placeholder="Age max"
        aria-label="Maximum age at creation"
        style={{ width: 96 }}
        data-testid="artwork-tiles-filter-age-max"
      />
      <Segmented
        size="small"
        options={VERSION_OPTIONS}
        value={versions}
        onChange={(value) => setVersions(String(value))}
        data-testid="artwork-tiles-versions"
      />
      {/* Wrapped: antd puts a Checkbox's data-* on the inner input, whose
          text content is empty — the span carries the testid AND the label. */}
      {hiddenCount > 0 ? (
        <span data-testid="artwork-tiles-show-hidden">
          <Checkbox
            checked={showHidden}
            onChange={(event) => setShowHidden(event.target.checked)}
          >
            Show hidden ({hiddenCount})
          </Checkbox>
        </span>
      ) : null}
      {activeFilterCount > 0 || versions !== VERSIONS.ALL || showHidden ? (
        <Button
          size="small"
          icon={<ClearOutlined />}
          onClick={clearFilters}
          data-testid="artwork-tiles-clear-filters"
        >
          Clear
        </Button>
      ) : null}
    </Flex>
  )

  /**
   * Count, then size controls (with Full screen leading in the tab), then
   * filters — one sticky block per surface, each with its own slider value.
   */
  const buildControls = (
    dark: boolean,
    withFullscreenButton: boolean,
    width: number,
    onWidthChange: (value: number) => void
  ) => (
    <>
      <Text
        type="secondary"
        style={secondaryTextStyle(dark)}
        data-testid="artwork-tiles-count"
      >
        {summary}
      </Text>
      <Flex gap={12} style={{ alignItems: "center" }}>
        {withFullscreenButton ? (
          <Button
            icon={<FullscreenOutlined />}
            onClick={openFullscreen}
            data-testid="artwork-tiles-fullscreen"
          >
            Full screen
          </Button>
        ) : null}
        <TileWidthSlider
          tileWidth={width}
          dark={dark}
          onChange={onWidthChange}
        />
      </Flex>
      {buildFilterRow()}
    </>
  )

  const buildBody = (dark: boolean, width: number): ReactNode =>
    visibleCount === 0 ? (
      <Empty
        description="No artwork matches these filters"
        style={{ padding: 24 }}
        data-testid="artwork-tiles-no-matches"
      />
    ) : (
      <ArtworkTileGrid
        sections={sections}
        studentId={student?.id ?? ""}
        tileWidth={width}
        dark={dark}
        addingId={addingId}
        hidingId={hidingId}
        onAddToPortfolio={onAddToPortfolio ? handleAddToPortfolio : undefined}
        onHide={onHide ? handleHide : undefined}
      />
    )

  return (
    <Flex
      col
      style={{ flex: 1, minHeight: 0 }}
      data-testid="artwork-tiles-section-root"
    >
      {/* The artwork container scrolls, not the whole tab; the controls ride
          on top as a sticky header. */}
      <SScrollArea data-testid="artwork-tiles-scroll">
        <SStickyControls $dark={false}>
          {buildControls(false, true, tileWidth, setTileWidth)}
        </SStickyControls>
        {buildBody(false, tileWidth)}
      </SScrollArea>

      <Modal
        open={isFullscreen}
        onCancel={closeFullscreen}
        footer={null}
        destroyOnClose
        width="96vw"
        style={{ top: 16, maxWidth: "none", paddingBottom: 0 }}
        styles={{
          body: {
            height: "calc(100vh - 130px)",
            overflowY: "auto",
            background: FULLSCREEN_BG,
            padding: "0 12px 12px",
          },
        }}
        title="Artwork"
      >
        {/* Same filtered sections as the tab, so opening fullscreen never
            changes what is on screen — only how big it is. The image URLs are
            identical too, so the browser serves them from cache rather than
            re-downloading. */}
        <div data-testid="artwork-tiles-fullscreen-body">
          <SStickyControls $dark>
            {buildControls(
              true,
              false,
              fullscreenTileWidth,
              setFullscreenTileWidth
            )}
          </SStickyControls>
          {buildBody(true, fullscreenTileWidth)}
        </div>
      </Modal>
    </Flex>
  )
}

export default StudentArtworkTilesSection
